import { TellescopeModelFromName } from "@tellescope/tanstack-query";
import { cn } from "@tellescope/ui";
import { addMinutes } from "date-fns/addMinutes";
import { endOfDay } from "date-fns/endOfDay";
import { startOfDay } from "date-fns/startOfDay";
import { MonthViewEventCell } from "../event-cells";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useDroppable } from "@dnd-kit/core";

export interface MonthlyViewDayCellProps {
  date: Date;
  dayNumber: number;
  isOtherMonth: boolean;
  events: TellescopeModelFromName<"calendar_events">[];
  className?: string;
  getEventOnClick?: (eventId: string) => (() => void) | { to: string; replace?: boolean };
  onDayClick?: (() => void) | string;
  timezone?: string;
  variant?: "compact" | "default";
}

export const MonthlyViewDayCell = ({
  date,
  dayNumber,
  isOtherMonth,
  events,
  className,
  getEventOnClick,
  onDayClick,
  timezone,
  variant = "default",
}: MonthlyViewDayCellProps) => {
  const { t } = useTranslation("calendar");
  const dateStart = startOfDay(date);
  const dateEnd = endOfDay(date);
  const eventsToDisplay = events.slice(0, 3);
  const otherEventsCount = events.length - eventsToDisplay.length;

  const { setNodeRef, isOver } = useDroppable({
    id: `month-day-${dateStart.getTime()}`,
    data: { dayDate: dateStart },
  });

  const Component = onDayClick ? (typeof onDayClick === "string" ? Link : "button") : "div";

  return (
    <Component
      ref={setNodeRef}
      data-testid={`calendar-month-day-${dateStart.getTime()}`}
      className={cn("py-1.5", !!onDayClick && "hover:bg-gray-50", isOver && "bg-primary/5", className)}
      to={typeof onDayClick === "string" && typeof Component !== "string" ? onDayClick : ""}
      onClick={typeof onDayClick === "function" && Component === "button" ? onDayClick : undefined}
    >
      <div className={cn("flex flex-col gap-1", variant === "compact" && "h-full justify-between")}>
        <div className="flex flex-col gap-1">
          <div className={cn("px-2 justify-start items-center text-xs", isOtherMonth && "opacity-20")}>{dayNumber}</div>
          <div
            className={cn(
              "flex flex-col gap-1.5",
              isOtherMonth && "opacity-50",
              variant === "compact" && "flex-row p-2"
            )}
          >
            {eventsToDisplay.map((event) => {
              const eventStartDate = new Date(event.startTimeInMS);
              const eventEndDate = addMinutes(eventStartDate, event.actualDuration ?? event.durationInMinutes);
              const fromPrevious = eventStartDate.getTime() < dateStart.getTime();
              const continuesToNext = eventEndDate.getTime() > dateEnd.getTime();
              const isCancelled = !!event.cancelledAt || !!event.rescheduledAt;

              if (variant === "compact") {
                return (
                  <span
                    key={`${event.id}-${dateStart.getTime()}`}
                    className="rounded-full size-2 bg-gray-500"
                    style={{ backgroundColor: event.color }}
                  />
                );
              }

              return (
                <MonthViewEventCell
                  key={`${event.id}-${dateStart.getTime()}`}
                  event={event}
                  fromPrevious={fromPrevious}
                  continuesToNext={continuesToNext}
                  onClick={getEventOnClick?.(event.id)}
                  timezone={timezone}
                  draggable={!isCancelled}
                  sourceDayStart={dateStart.getTime()}
                />
              );
            })}
            {!!otherEventsCount && variant !== "compact" && (
              <div className="text-xs text-muted-foreground font-semibold px-2">
                {t("{{amount}} more...", { amount: otherEventsCount })}
              </div>
            )}
          </div>
        </div>

        {!!otherEventsCount && variant === "compact" && (
          <div className="text-xs text-muted-foreground font-semibold px-2">
            {t("{{amount}} more...", { amount: otherEventsCount })}
          </div>
        )}
      </div>
    </Component>
  );
};
